import { useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useDiceStore, type DiceFormula } from '@/stores/diceStore'
import { useThemeStore } from '@/stores/themeStore'
import { playSfx } from '@/features/audio/AudioEngine'

const SETTLE_MS = 1500

function localRoll(f: DiceFormula): number {
  let sum = f.modifier
  for (let i = 0; i < f.count; i++) sum += 1 + Math.floor(Math.random() * f.sides)
  return sum
}

type DieType = 'd4' | 'd6' | 'd8' | 'd10' | 'd12' | 'd20'

/** Map a die's side count to the polyhedron we render for it. */
function sidesToType(sides: number): DieType {
  switch (sides) {
    case 4:
      return 'd4'
    case 6:
      return 'd6'
    case 8:
      return 'd8'
    case 10:
      return 'd10'
    case 12:
      return 'd12'
    case 20:
      return 'd20'
    default:
      // d% / d100 and other oddballs read best as a d10, big ones as a d20.
      return sides <= 10 ? 'd10' : 'd20'
  }
}

interface DieSpec {
  start: THREE.Vector3
  rest: THREE.Vector3
  axis: THREE.Vector3
  speed: number
  geo: DieType
}

/** The shared Grymwyrd die material. */
function DieMaterial() {
  return <meshStandardMaterial color="#1b1530" emissive="#44346D" emissiveIntensity={0.35} roughness={0.4} metalness={0.3} flatShading />
}

/** The correct shape for each die. d10 is a pentagonal bipyramid (two 5-gon cones). */
function DieMeshes({ type }: { type: DieType }) {
  switch (type) {
    case 'd4':
      return (
        <mesh castShadow>
          <tetrahedronGeometry args={[0.62, 0]} />
          <DieMaterial />
        </mesh>
      )
    case 'd6':
      return (
        <mesh castShadow>
          <boxGeometry args={[0.7, 0.7, 0.7]} />
          <DieMaterial />
        </mesh>
      )
    case 'd8':
      return (
        <mesh castShadow>
          <octahedronGeometry args={[0.6, 0]} />
          <DieMaterial />
        </mesh>
      )
    case 'd12':
      return (
        <mesh castShadow>
          <dodecahedronGeometry args={[0.55, 0]} />
          <DieMaterial />
        </mesh>
      )
    case 'd20':
      return (
        <mesh castShadow>
          <icosahedronGeometry args={[0.5, 0]} />
          <DieMaterial />
        </mesh>
      )
    case 'd10':
      return (
        <>
          <mesh castShadow position={[0, 0.2, 0]}>
            <coneGeometry args={[0.5, 0.5, 5]} />
            <DieMaterial />
          </mesh>
          <mesh castShadow position={[0, -0.2, 0]} rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[0.5, 0.5, 5]} />
            <DieMaterial />
          </mesh>
        </>
      )
  }
}

/** A single tumbling die: slides in from the right, spins down to rest. */
function Die({ spec, reduced }: { spec: DieSpec; reduced: boolean }) {
  const ref = useRef<THREE.Group>(null)
  const vel = useRef(spec.speed)
  useFrame((state, delta) => {
    const m = ref.current
    if (!m) return
    const t = state.clock.getElapsedTime()
    const p = THREE.MathUtils.clamp(t / (SETTLE_MS / 1000), 0, 1)
    const ease = 1 - Math.pow(1 - p, 3)
    m.position.lerpVectors(spec.start, spec.rest, ease)
    if (p < 1) m.position.y += Math.abs(Math.sin(t * 7)) * (1 - p) * 0.7 // bounce
    if (reduced) {
      m.position.copy(spec.rest)
      return
    }
    if (p >= 1) vel.current = Math.max(0, vel.current - delta * spec.speed * 2.5)
    m.rotation.x += spec.axis.x * vel.current * delta
    m.rotation.y += spec.axis.y * vel.current * delta
    m.rotation.z += spec.axis.z * vel.current * delta
  })
  return (
    <group ref={ref} position={spec.start}>
      <DieMeshes type={spec.geo} />
    </group>
  )
}

function DiceScene({ specs, reduced }: { specs: DieSpec[]; reduced: boolean }) {
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[3, 5, 4]} intensity={1.4} color="#f0e2c0" />
      <pointLight position={[-3, 2, 3]} intensity={0.8} color="#9482ba" />
      {specs.map((s, i) => (
        <Die key={i} spec={s} reduced={reduced} />
      ))}
    </>
  )
}

/**
 * Full-viewport dice roller. When a roll is requested (diceStore), dice tumble
 * in from the right across the page "like a table", the authoritative total is
 * fetched from the RPG API, and `onResolved(total)` fires once they settle.
 */
export function DiceOverlay() {
  const request = useDiceStore((s) => s.request)
  const clear = useDiceStore((s) => s.clear)
  const reduced = useThemeStore((s) => s.reducedMotion)
  const [total, setTotal] = useState<number | null>(null)
  const [settled, setSettled] = useState(false)

  // Lay out `count` dice resting in a centred row, entering from the right.
  const specs = useMemo<DieSpec[]>(() => {
    if (!request) return []
    const { count, sides } = request.formula
    const geo = sidesToType(sides)
    const span = (count - 1) * 1.1
    return Array.from({ length: count }, (_, i) => ({
      start: new THREE.Vector3(7 + i * 0.8, 2.4 + Math.random(), (Math.random() - 0.5) * 1.5),
      rest: new THREE.Vector3(i * 1.1 - span / 2, -0.4, 0),
      axis: new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize(),
      speed: 9 + Math.random() * 6,
      geo,
    }))
  }, [request])

  // Fetch the authoritative total + arm the settle timer.
  useEffect(() => {
    if (!request) return
    setTotal(null)
    setSettled(false)
    if (!reduced) playSfx('dice')
    const timer = setTimeout(() => setSettled(true), reduced ? 0 : SETTLE_MS)
    let cancelled = false
    fetch('/api/rpg/dice/roll', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ formula: request.formula.raw }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => !cancelled && setTotal(typeof j?.value === 'number' ? j.value : localRoll(request.formula)))
      .catch(() => !cancelled && setTotal(localRoll(request.formula)))
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [request, reduced])

  // Resolve once the dice have settled AND the total is known.
  useEffect(() => {
    if (!request || !settled || total == null) return
    request.onResolved?.(total)
    const t = setTimeout(() => clear(), 1900)
    return () => clearTimeout(t)
  }, [request, settled, total, clear])

  if (!request) return null
  const showResult = settled && total != null

  return (
    <div className="pointer-events-none fixed inset-0 z-[60]" aria-hidden>
      <Canvas key={request.id} camera={{ position: [0, 0.5, 8], fov: 45 }} gl={{ alpha: true, antialias: true }} style={{ background: 'transparent' }}>
        <DiceScene specs={specs} reduced={reduced} />
      </Canvas>

      {showResult && (
        <div className="absolute inset-x-0 bottom-[18%] flex justify-center" role="status" aria-live="polite">
          <div className="surface-parchment animate-pulse-blood border border-gold/50 px-6 py-3 text-center shadow-glow-gold">
            <p className="font-ui text-[10px] uppercase tracking-title text-bone-dim">{request.label ?? request.formula.raw}</p>
            <p className="font-gothic text-4xl text-gold">{total}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default DiceOverlay
